/**
 * Parseo y validación del formulario de equipos (base `/api/teams`).
 *
 * El plantel se edita como TEXTO, una línea por jugador (`14 Palacios punta`):
 * cargar 14 jugadores con inputs sueltos es un suplicio, y el cuerpo técnico
 * ya tiene la lista escrita en algún lado para pegar. Todo el parseo vive acá,
 * puro y testeable; el componente sólo muestra errores.
 *
 * El resultado es EXACTAMENTE el contrato `TeamInfo` de `@scout/core`, que es
 * el mismo objeto que viaja dentro de `match_created.payload.home/away`.
 */
import type { Player, TeamInfo } from '@scout/core';
import { getT, type MessageKey, type TFunction } from '../i18n/index';

export type PlayerRole = NonNullable<Player['role']>;

/**
 * Alias → rol del contrato. Se aceptan los tres idiomas SIEMPRE (sin importar
 * el idioma de la interfaz): un plantel pegado de otro lado no tiene por qué
 * venir en el idioma que eligió este navegador.
 */
const ROLE_ALIASES: Record<string, PlayerRole> = {
  setter: 'setter',
  armador: 'setter',
  arm: 'setter',
  levantador: 'setter',
  outside: 'outside',
  punta: 'outside',
  receptor: 'outside',
  ponteiro: 'outside',
  opposite: 'opposite',
  opuesto: 'opposite',
  oposto: 'opposite',
  middle: 'middle',
  central: 'middle',
  libero: 'libero',
  'líbero': 'libero',
  unknown: 'unknown',
};

const ROLE_KEYS: Record<PlayerRole, MessageKey> = {
  setter: 'teams.roles.setter',
  outside: 'teams.roles.outside',
  opposite: 'teams.roles.opposite',
  middle: 'teams.roles.middle',
  libero: 'teams.roles.libero',
  unknown: 'teams.roles.unknown',
};

/** Etiquetas visibles de los roles, en el idioma activo. */
export function roleLabels(t: TFunction = getT()): Record<PlayerRole, string> {
  return {
    setter: t(ROLE_KEYS.setter),
    outside: t(ROLE_KEYS.outside),
    opposite: t(ROLE_KEYS.opposite),
    middle: t(ROLE_KEYS.middle),
    libero: t(ROLE_KEYS.libero),
    unknown: t(ROLE_KEYS.unknown),
  };
}

export function roleLabel(role: PlayerRole, t: TFunction = getT()): string {
  return t(ROLE_KEYS[role]);
}

export function parseRole(raw: string): PlayerRole | null {
  const key = raw.trim().toLowerCase();
  if (key === '') return null;
  return ROLE_ALIASES[key] ?? null;
}

export interface ParsedPlayers {
  players: Player[];
  errors: string[];
}

/**
 * Una línea por jugador: `<número> <nombre…> [rol]`.
 * El rol es opcional y va al final; si la última palabra no es un rol
 * conocido, se considera parte del nombre (no se adivina ni se descarta).
 */
export function parsePlayersText(text: string, t: TFunction = getT()): ParsedPlayers {
  const players: Player[] = [];
  const errors: string[] = [];
  const seen = new Set<number>();

  text.split('\n').forEach((rawLine, index) => {
    const line = rawLine.trim();
    if (line === '') return;
    const lineNo = index + 1;

    const tokens = line.split(/\s+/);
    const first = tokens[0] ?? '';
    const number = Number(first.replace(/^#/, ''));
    if (!Number.isInteger(number) || number < 0 || number > 99) {
      errors.push(t('teams.errors.badNumber', { line: lineNo, token: first }));
      return;
    }
    if (seen.has(number)) {
      errors.push(t('teams.errors.repeatedNumber', { line: lineNo, number }));
      return;
    }
    seen.add(number);

    const rest = tokens.slice(1);
    let role: PlayerRole | undefined;
    if (rest.length > 1) {
      const maybeRole = parseRole(rest[rest.length - 1] ?? '');
      if (maybeRole) {
        role = maybeRole;
        rest.pop();
      }
    }
    const name = rest.join(' ').trim();
    players.push({
      number,
      ...(name !== '' ? { name } : {}),
      ...(role !== undefined ? { role } : {}),
    });
  });

  return { players, errors };
}

/** Vuelve al texto editable (para cargar el formulario desde el API). */
export function playersToText(players: readonly Player[], t: TFunction = getT()): string {
  return players
    .map((p) => [String(p.number), p.name ?? '', p.role ? roleLabel(p.role, t) : ''].join(' ').trim())
    .join('\n');
}

export interface ParsedLiberos {
  liberos: number[];
  errors: string[];
}

/** `6, 12` o `6 12` → `[6, 12]`. */
export function parseLiberosText(text: string, t: TFunction = getT()): ParsedLiberos {
  const liberos: number[] = [];
  const errors: string[] = [];
  for (const raw of text.split(/[\s,]+/)) {
    const token = raw.trim();
    if (token === '') continue;
    const number = Number(token.replace(/^#/, ''));
    if (!Number.isInteger(number) || number < 0 || number > 99) {
      errors.push(t('teams.errors.badLibero', { token }));
      continue;
    }
    if (!liberos.includes(number)) liberos.push(number);
  }
  return { liberos, errors };
}

/** El API sanea el id porque es nombre de archivo. */
export function isValidTeamId(id: string): boolean {
  return /^[A-Za-z0-9._-]+$/.test(id);
}

/** `Ciudad Vóley` → `ciudad-voley`, para proponer un id al crear. */
export function slugifyTeamId(name: string): string {
  return name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
}

export interface TeamFormValues {
  teamId: string;
  name: string;
  code: string;
  coach: string;
  playersText: string;
  liberosText: string;
}

export const EMPTY_TEAM_FORM: TeamFormValues = {
  teamId: '',
  name: '',
  code: '',
  coach: '',
  playersText: '',
  liberosText: '',
};

export type TeamFormResult =
  | { ok: true; teamId: string; info: TeamInfo }
  | { ok: false; errors: string[] };

/** Valida el formulario entero y arma el body del PUT. */
export function buildTeamPayload(
  values: TeamFormValues,
  t: TFunction = getT(),
): TeamFormResult {
  const errors: string[] = [];
  const teamId = values.teamId.trim();
  const name = values.name.trim();

  if (teamId === '') errors.push(t('teams.errors.idRequired'));
  else if (!isValidTeamId(teamId)) {
    errors.push(t('teams.errors.idInvalid'));
  }
  if (name === '') errors.push(t('teams.errors.nameRequired'));
  if (values.code.trim().length > 8) errors.push(t('teams.errors.codeTooLong'));

  const players = parsePlayersText(values.playersText, t);
  const liberos = parseLiberosText(values.liberosText, t);
  errors.push(...players.errors, ...liberos.errors);

  const numbers = new Set(players.players.map((p) => p.number));
  for (const libero of liberos.liberos) {
    if (!numbers.has(libero)) {
      errors.push(t('teams.errors.liberoNotInRoster', { number: libero }));
    }
  }

  if (errors.length > 0) return { ok: false, errors };

  const code = values.code.trim();
  const coach = values.coach.trim();
  return {
    ok: true,
    teamId,
    info: {
      name,
      ...(code !== '' ? { code } : {}),
      ...(coach !== '' ? { coach } : {}),
      players: players.players,
      liberos: liberos.liberos,
    },
  };
}
